import { useCallback, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  selectAllBoundingBoxes,
  selectedBBoxClassIndex,
  setSelectedBoundingBox,
  selectBoundingBoxStatuses,
  BBoxStatus,
} from '../../../../store/slices/annotationSlice'
import { AnnotationDataBoundingBox } from '../../../../types/models/annotation'

export type RightPanelTab = 'tags' | 'snippets'

export interface TagLite {
  id: string
  name: string
}

interface UseAnnotationsReviewParams {
  tags?: TagLite[]
}

export function useAnnotationsReview({ tags }: UseAnnotationsReviewParams = {}) {
  const dispatch = useAppDispatch()

  // Redux selectors
  const allBoxesRaw = useAppSelector(selectAllBoundingBoxes)
  const statuses = useAppSelector(selectBoundingBoxStatuses)
  const allBoundingBoxes = useMemo(() => {
    // Filter out rejected for display and submission in Annotations Review
    return allBoxesRaw.filter((b) => statuses[b.class_index] !== BBoxStatus.Rejected)
  }, [allBoxesRaw, statuses])
  const selectedBBoxClassIdx = useAppSelector(selectedBBoxClassIndex)

  // Local UI state
  const [selectedSnippetIndex, setSelectedSnippetIndex] = useState<number | undefined>(undefined)
  const [hoveredBoxIndex, setHoveredBoxIndex] = useState<number | undefined>(undefined)
  const [activeRightTab, setActiveRightTab] = useState<RightPanelTab>('snippets')
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set())

  // Derived lists
  const uniqueTags = useMemo(() => {
    return Array.from(new Set(allBoundingBoxes.map((box) => box.name)))
  }, [allBoundingBoxes])

  const visibleBoxes = useMemo(() => {
    // If tags are expanded, show all boxes for those tags
    if (expandedTags.size > 0 && tags?.length) {
      return allBoundingBoxes.filter((box) => {
        const tag = tags.find((t) => t.id === box.name)
        return tag && expandedTags.has(tag.id)
      })
    }

    // If no tag is expanded and no box is selected, show all boxes
    return allBoundingBoxes
  }, [allBoundingBoxes, expandedTags, tags])

  const annotationTagIds = useMemo(() => {
    return Array.from(new Set(allBoundingBoxes.map((a) => a.name)))
  }, [allBoundingBoxes])

  // Helpers
  const clearSelection = useCallback(() => {
    setSelectedSnippetIndex(undefined)
    dispatch(setSelectedBoundingBox(undefined))
  }, [dispatch])

  const getCurrentSelectedBox = useCallback((): AnnotationDataBoundingBox | undefined => {
    return allBoundingBoxes.find((b) => b.class_index === selectedBBoxClassIdx)
  }, [selectedBBoxClassIdx, allBoundingBoxes])

  const getFocusedIndexForVisibleList = useCallback((): number | undefined => {
    if (selectedBBoxClassIdx == null) return undefined
    const idx = visibleBoxes.findIndex((b) => b.class_index === selectedBBoxClassIdx)
    return idx >= 0 ? idx : undefined
  }, [visibleBoxes, selectedBBoxClassIdx])

  const selectByClassIndex = useCallback(
    (classIndex: number | undefined) => {
      if (classIndex == null) {
        clearSelection()
        return
      }
      const box = allBoundingBoxes.find((b) => b.class_index === classIndex)
      if (!box) {
        clearSelection()
        return
      }
      dispatch(setSelectedBoundingBox(box))
      const idxInVisible = visibleBoxes.findIndex((b) => b.class_index === classIndex)
      setSelectedSnippetIndex(idxInVisible >= 0 ? idxInVisible : undefined)
    },
    [allBoundingBoxes, visibleBoxes, dispatch, clearSelection]
  )

  const selectNeighborInVisibleList = useCallback(
    (delta: 1 | -1) => {
      const list = visibleBoxes
      if (list.length === 0) {
        clearSelection()
        return
      }
      const currentIdx = getFocusedIndexForVisibleList()
      const nextIdx =
        currentIdx == null
          ? delta === 1
            ? 0
            : list.length - 1
          : Math.min(Math.max(currentIdx + delta, 0), list.length - 1)
      const nextBox = list[nextIdx]
      if (nextBox) {
        dispatch(setSelectedBoundingBox(nextBox))
        setSelectedSnippetIndex(nextIdx)
      }
    },
    [visibleBoxes, dispatch, getFocusedIndexForVisibleList, clearSelection]
  )

  const toggleTagExpansion = useCallback(
    (tagId: string) => {
      // Clear any selected box when toggling tags
      dispatch(setSelectedBoundingBox(undefined))
      setExpandedTags((prev) => {
        const next = new Set(prev)
        if (next.has(tagId)) {
          next.delete(tagId)
        } else {
          // Only one expanded at a time (current behavior)
          next.clear()
          next.add(tagId)
        }
        return next
      })
    },
    [dispatch]
  )

  const handleTagSelect = useCallback(
    (box: AnnotationDataBoundingBox) => {
      if (selectedBBoxClassIdx === box.class_index) {
        clearSelection()
      } else {
        dispatch(setSelectedBoundingBox(box))
        // Always resolve the selection index relative to the full allBoundingBoxes
        const idxInAll = allBoundingBoxes.findIndex((b) => b.class_index === box.class_index)
        setSelectedSnippetIndex(idxInAll >= 0 ? idxInAll : undefined)
      }
    },
    [selectedBBoxClassIdx, clearSelection, dispatch, allBoundingBoxes]
  )

  const resetViewState = useCallback(() => {
    // Reset only local UI state; do not mutate Redux annotation data here
    setSelectedSnippetIndex(undefined)
    setHoveredBoxIndex(undefined)
    setActiveRightTab('snippets')
    setExpandedTags(new Set())
  }, [])

  return {
    // state
    selectedSnippetIndex,
    setSelectedSnippetIndex,
    hoveredBoxIndex,
    setHoveredBoxIndex,
    activeRightTab,
    setActiveRightTab,
    expandedTags,
    setExpandedTags,
    // selectors
    allBoundingBoxes,
    selectedBBoxClassIdx,
    visibleBoxes,
    uniqueTags,
    annotationTagIds,
    // helpers
    clearSelection,
    getCurrentSelectedBox,
    getFocusedIndexForVisibleList,
    selectByClassIndex,
    selectNeighborInVisibleList,
    toggleTagExpansion,
    handleTagSelect,
    resetViewState,
  }
}

export default useAnnotationsReview
